import { useCallback, useEffect, useMemo, useState } from "react";

import { useReportsActions } from "store";
import { Report } from "store/types";

interface UseReportsByGroupsResult {
  reportsByGroup: Map<string, Report[]>;
  getReportsByGroup: (groupName: string) => Report[];
}

/**
 * Hook to fetch reports for multiple groups
 * Replaces ReportsProvider context functionality
 */
export const useReportsByGroups = (groups: string[]): UseReportsByGroupsResult => {
  const [reportsByGroup, setReportsByGroup] = useState<Map<string, Report[]>>(new Map());
  const { getReportsByGroupAction } = useReportsActions();

  // Memoize groups for stable dependency comparison
  const groupsKey = useMemo(() => groups.join(","), [groups]);

  // Fetch all reports for the specified groups
  useEffect(() => {
    if (!groups.length) return;

    const fetchReports = async () => {
      try {
        // Fetch reports for all groups (no context parameters)
        const fetchPromises = groups.map(async (groupName) => {
          try {
            const reports = await getReportsByGroupAction(groupName);
            return { groupName, reports };
          } catch (error) {
            // If a specific group fails, return empty array for that group
            console.warn(`[useReportsByGroups] Failed to fetch reports for group "${groupName}":`, error);
            return { groupName, reports: [] };
          }
        });

        const results = await Promise.all(fetchPromises);

        // Convert to Map
        const reportsMap = new Map<string, Report[]>();
        results.forEach(({ groupName, reports }) => {
          reportsMap.set(groupName, reports as unknown as Report[]);
        });

        setReportsByGroup(reportsMap);
      } catch (error) {
        console.error("[useReportsByGroups] Failed to fetch reports:", error);
      }
    };

    fetchReports();
  }, [groupsKey]);

  const getReportsByGroup = useCallback(
    (groupName: string): Report[] => reportsByGroup.get(groupName) ?? [],
    [reportsByGroup]
  );

  return { reportsByGroup, getReportsByGroup };
};
